<script setup>
import {inject, ref, watch} from 'vue';
import {ArrowLeftIcon} from '@heroicons/vue/24/solid';
import SubmitButtonLargeComponent from '@buttons/SubmitButtonLargeComponent.vue';
import {debounce} from 'lodash';
import {showErrorToast, showSuccessToast} from '@utils/Toast';
import MainSiteLogo from '../Includes/menu/MainSiteLogo.vue';

const props = defineProps({
    csrf: String,
    route: String,
    routeSuccess: String,
});

// Inject Axios
const axios = inject('axios');

const passwordRegex = /^(?=\S*$)(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*\-]).{10,20}$/;

//Handle with form
const email = ref('');
const password = ref('');
const passwordAgain = ref('');

const errors = ref({
    email: null,
    password: null,
    passwordAgain: null,
    bothPasswordMatch: null,
});

const isFormValid = ref(false);

const delayedRedirection = debounce(() => {
    window.location.href = props.routeSuccess;
}, 2000);

const validateForm = () => {
    // Check the validity of the input fields
    const isEmailValid = email.value !== '';
    const isPasswordValid = password.value !== '' && passwordRegex.test(password.value);
    const isPasswordAgainValid = passwordAgain.value !== '' && passwordRegex.test(passwordAgain.value);
    const bothPasswordMatch = (isPasswordValid && isPasswordAgainValid && password.value === passwordAgain.value) ||
        password.value === '' || passwordAgain.value === '';

    errors.value.email = !isEmailValid ? 'Email address is required' : null;
    errors.value.password = !isPasswordValid ? 'Invalid password format' : null;
    errors.value.passwordAgain = !isPasswordAgainValid ? 'Invalid password format' : null;
    errors.value.bothPasswordMatch = !bothPasswordMatch ? 'Passwords do not match' : null;

    isFormValid.value = isEmailValid && isPasswordValid && isPasswordAgainValid && bothPasswordMatch;
};

const submitForm = async () => {
    validateForm();
    if(!isFormValid.value) {
        // showErrorToast('Invalid form values');
        return false;
    }

    //Prepare the data to be sent
    const formData = {
        email: email.value,
        password: password.value,
        password_confirmation: passwordAgain.value,
    };

    try {
        //Send a POST request to the server
        const response = await axios.post(props.route, formData, {
            headers: {
                'X-CSRF-TOKEN': props.csrf // Include CSRF token here
            }
        });
        //Handle response here
        if(response.status === 200) {
            const data = response.data;
            if(data.success === 1) {
                showSuccessToast(data.message);
                delayedRedirection();
            } else {
                showErrorToast(data.message);
            }
        } else {
            if(response.message) {
                showErrorToast(response.message);
            }
        }

    } catch (error) {
        console.error('Submission error', error);
    }
};

</script>

<template>
    <div>
        <div class="text-center container max-w-100 md:w-100">
            <div class="wrap-form">
                <form :action="route" id="frmClientLogin" method="post"
                      @submit.prevent="submitForm"
                >
                    <div class="flex flex-col justify-between py-6 mb-6 md:mb-0 text-center">
                        <div>
                            <div class="font-bold text-2xl mb-6 leading-tight">Reset Password</div>

                            <div class="form-row">
                                <input type="email"
                                       name="email"
                                       v-model="email"
                                       class="form-control z-10 px-8"
                                       placeholder="Email Address"
                                       autocomplete="off"
                                >
                                <div v-if="errors.email" class="py-2 text-red text-left" v-text="errors.email"></div>
                            </div>
                            <div class="form-row">
                                <input type="password"
                                       name="password"
                                       v-model="password"
                                       class="form-control z-10"
                                       placeholder="Password"
                                       autocomplete="off"
                                >
                                <div v-if="errors.password" class="py-2 text-red text-left"
                                     v-text="errors.password"></div>
                            </div>
                            <div class="form-row !mb-6">
                                <input type="password"
                                       name="password_confirmation"
                                       v-model="passwordAgain"
                                       class="form-control z-10"
                                       placeholder="Re-Type Password"
                                       autocomplete="off"
                                >
                                <div v-if="errors.passwordAgain" class="py-2 text-red text-left"
                                     v-text="errors.passwordAgain"></div>
                                <div v-if="errors.bothPasswordMatch" class="py-2 text-red text-left"
                                     v-text="errors.bothPasswordMatch"></div>
                            </div>
                        </div>
                        <div>
                            <SubmitButtonLargeComponent
                                title="Reset Password"
                                btn-class="btn-green-5 w-full !py-4"
                            >
                            </SubmitButtonLargeComponent>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.form {
    &-row {
        @apply mb-4;
    }
    &-control {
        @apply bg-white py-3;
    }
}
</style>
